/**
 * Operation Queue Manager
 *
 * Queues operations when offline and retries them when back online.
 *
 * Requirements: 5.4
 */

import { getNetworkStatusManager } from './networkStatus'
import { loadLogger } from './loadLogger'

// Use a safe logger that doesn't throw if not initialized
const safeLogger = {
    info: (message: string, ...args: unknown[]) => {
        try {
            loadLogger().info(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
    warn: (message: string, ...args: unknown[]) => {
        try {
            loadLogger().warn(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
    error: (message: string, ...args: unknown[]) => {
        try {
            loadLogger().error(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
    debug: (message: string, ...args: unknown[]) => {
        try {
            loadLogger().debug(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
}

/**
 * Queued operation
 */
export interface QueuedOperation {
    /** Unique ID for the operation */
    id: string

    /** Operation type/name for logging */
    type: string

    /** Function to execute */
    execute: () => Promise<unknown>

    /** Timestamp when queued */
    queuedAt: number

    /** Number of retry attempts */
    retryCount: number

    /** Optional metadata */
    metadata?: Record<string, unknown>
}

/**
 * Operation Queue Manager
 *
 * Manages a queue of operations to be executed when network is available.
 */
export class OperationQueueManager {
    private static instance: OperationQueueManager | null = null
    private queue: QueuedOperation[] = []
    private isProcessing: boolean = false
    private maxRetries: number = 3
    private retryDelay: number = 1000 // 1 second
    private unsubscribeNetworkListener?: () => void

    private constructor() {
        this.setupNetworkListener()
    }

    /**
     * Get or create singleton instance
     */
    static getInstance(): OperationQueueManager {
        if (!OperationQueueManager.instance) {
            OperationQueueManager.instance = new OperationQueueManager()
        }
        return OperationQueueManager.instance
    }

    /**
     * Reset singleton instance (useful for testing)
     */
    static resetInstance(): void {
        if (OperationQueueManager.instance) {
            OperationQueueManager.instance.cleanup()
            OperationQueueManager.instance = null
        }
    }

    /**
     * Setup network status listener
     */
    private setupNetworkListener(): void {
        const networkManager = getNetworkStatusManager()

        this.unsubscribeNetworkListener = networkManager.addListener(
            (isOnline) => {
                if (isOnline && this.queue.length > 0) {
                    safeLogger.info(
                        'Network restored, processing queued operations',
                        {
                            queueSize: this.queue.length,
                        }
                    )
                    this.processQueue()
                }
            }
        )
    }

    /**
     * Add an operation to the queue
     */
    enqueue(
        operation: Omit<QueuedOperation, 'id' | 'queuedAt' | 'retryCount'>
    ): string {
        const id = this.generateId()
        const queuedOperation: QueuedOperation = {
            ...operation,
            id,
            queuedAt: Date.now(),
            retryCount: 0,
        }

        this.queue.push(queuedOperation)

        safeLogger.info('Operation queued', {
            operationId: id,
            operationType: operation.type,
            queueSize: this.queue.length,
        })

        // Try to process immediately if online
        if (getNetworkStatusManager().isOnline && !this.isProcessing) {
            this.processQueue()
        }

        return id
    }

    /**
     * Remove an operation from the queue
     */
    dequeue(id: string): boolean {
        const index = this.queue.findIndex((op) => op.id === id)
        if (index !== -1) {
            this.queue.splice(index, 1)
            safeLogger.debug('Operation removed from queue', {
                operationId: id,
            })
            return true
        }
        return false
    }

    /**
     * Get all queued operations
     */
    getQueue(): QueuedOperation[] {
        return [...this.queue]
    }

    /**
     * Get queue size
     */
    getQueueSize(): number {
        return this.queue.length
    }

    /**
     * Clear all queued operations
     */
    clearQueue(): void {
        const size = this.queue.length
        this.queue = []
        safeLogger.info('Queue cleared', { clearedOperations: size })
    }

    /**
     * Process the queue
     */
    private async processQueue(): Promise<void> {
        if (this.isProcessing || this.queue.length === 0) {
            return
        }

        if (!getNetworkStatusManager().isOnline) {
            safeLogger.debug('Cannot process queue while offline')
            return
        }

        this.isProcessing = true
        safeLogger.info('Processing operation queue', {
            queueSize: this.queue.length,
        })

        // Process operations one at a time
        while (this.queue.length > 0 && getNetworkStatusManager().isOnline) {
            const operation = this.queue[0]

            try {
                safeLogger.debug('Executing queued operation', {
                    operationId: operation.id,
                    operationType: operation.type,
                    retryCount: operation.retryCount,
                })

                await operation.execute()

                // Success - remove from queue
                this.queue.shift()

                safeLogger.info('Queued operation completed successfully', {
                    operationId: operation.id,
                    operationType: operation.type,
                })
            } catch (error) {
                safeLogger.error('Queued operation failed', {
                    operationId: operation.id,
                    operationType: operation.type,
                    retryCount: operation.retryCount,
                    error,
                })

                // Increment retry count
                operation.retryCount++

                if (operation.retryCount >= this.maxRetries) {
                    // Max retries reached - remove from queue
                    this.queue.shift()
                    safeLogger.error(
                        'Queued operation failed after max retries',
                        {
                            operationId: operation.id,
                            operationType: operation.type,
                            maxRetries: this.maxRetries,
                        }
                    )
                } else {
                    // Wait before retrying
                    await this.delay(this.retryDelay * operation.retryCount)
                }
            }
        }

        this.isProcessing = false

        if (this.queue.length > 0) {
            safeLogger.info('Queue processing paused', {
                remainingOperations: this.queue.length,
                isOnline: getNetworkStatusManager().isOnline,
            })
        } else {
            safeLogger.info('Queue processing completed')
        }
    }

    /**
     * Generate a unique ID for an operation
     */
    private generateId(): string {
        return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }

    /**
     * Delay helper
     */
    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms))
    }

    /**
     * Cleanup
     */
    private cleanup(): void {
        if (this.unsubscribeNetworkListener) {
            this.unsubscribeNetworkListener()
        }
        this.clearQueue()
    }
}

/**
 * Get the operation queue manager instance
 */
export function getOperationQueueManager(): OperationQueueManager {
    return OperationQueueManager.getInstance()
}

/**
 * Queue an operation to be executed when online
 */
export function queueOperation(
    operation: Omit<QueuedOperation, 'id' | 'queuedAt' | 'retryCount'>
): string {
    return getOperationQueueManager().enqueue(operation)
}

/**
 * Get the current queue size
 */
export function getQueueSize(): number {
    return getOperationQueueManager().getQueueSize()
}
